/**
 * Echt · quick add on touch: warm the request, and show the press (7 Sep 2026).
 *
 * Basil, after PR #41 made quick add a one-tap control on touch: "I think a
 * problem may be the delay for the quick add dialogue to pop up, and the
 * highlight is on the whole card rather than the button when pressed."
 *
 * Two separate defects, and only one of them is a network problem.
 *
 * 1. THE DELAY. `assets/quick-add.js` `handleClick` fetches the whole product
 *    page (`fetch(productPageUrl)`), parses it with DOMParser and morphs its
 *    variant picker into the dialog before opening it. Stock warms that fetch
 *    on `pointerenter`/`focusin` — but `#handleQuickAdd` in
 *    `assets/product-card.js` binds those two listeners ONLY when
 *    `isDesktopBreakpoint()` (>= 750px). A phone therefore starts the whole
 *    round trip at the moment of the tap, and shows nothing until it lands: a
 *    second or more on 4G, with no feedback at all.
 *
 *    What this module does about it: on `pointerdown` — the earliest event in
 *    the tap, ~50-150 ms before `click` — it puts a
 *    `<link rel="preload" as="fetch" crossorigin>` for the same URL in the
 *    head. That is the ONLY mechanism available here, and the reason is in the
 *    response headers: a Shopify storefront page is served
 *    `cache-control: private, no-store` (verified against the live store,
 *    7 Sep 2026), so nothing HTTP-cacheable is possible — a duplicate
 *    `fetch()` on pointerdown would simply be a second request, not a warm
 *    one. A preload is matched out of the document's preload list, not out of
 *    the HTTP cache, and the match happens while the request is still IN
 *    FLIGHT, which is exactly the window that matters: on 4G the preload is
 *    still running when the click arrives, so stock's `fetch()` attaches to it
 *    instead of starting its own. The attributes are chosen to make that match
 *    possible: `as="fetch"` gives destination "empty" and `crossorigin`
 *    (anonymous) gives mode "cors" with credentials "same-origin" — the exact
 *    shape of a bare same-origin `fetch(url)`, cookies included, which the
 *    product page needs to render for the current cart and market.
 *
 *    RULED OUT, deliberately, so nobody re-adds them:
 *      - calling stock's own `quickAdd.fetchProductPage(url)` on pointerdown.
 *        It looks like the obvious warm (it is what stock's desktop prefetch
 *        and its variant-update path both call) but it is self-cancelling
 *        here: the method aborts the previous request through the component's
 *        own AbortController, so the click's call would abort the warm one it
 *        was supposed to reuse and start again from zero — a net loss.
 *      - dispatching a synthetic `pointerenter` at the card to trigger stock's
 *        prefetch. There is no listener to receive it: the bind is behind
 *        `isDesktopBreakpoint()`, which is false on the phones this is for.
 *      - a plain duplicate `fetch()`: see `no-store` above.
 *      - calling `handleClick` on pointerdown (which WOULD warm stock's own
 *        in-component cache). It opens the dialog before the finger lifts, so
 *        a scroll flick that happens to start on the button opens a modal.
 *
 *    If an engine declines to match the preload, the cost is one extra request
 *    for a page the shopper is opening anyway — bounded to one per URL per
 *    page view, and skipped entirely on a metered or slow connection, where a
 *    duplicate would compete with the request that matters.
 *
 * 2. THE HIGHLIGHT. The card is a link, so iOS paints its own tap highlight
 *    over the WHOLE card when anything inside it is pressed, and the button
 *    itself had no touch feedback beyond stock's `:active { scale: .99 }`.
 *    The highlight is killed in CSS (`assets/echt-bridge.css`); the pressed
 *    and "opening" states are drawn by
 *    `sections/echt-card-badges.liquid`. This module is what puts the classes
 *    on the button, rather than leaning on `:active` alone, for two reasons:
 *    iOS only applies `:active` on touch under its own heuristics, and the
 *    "opening" state has to persist for the whole fetch — which is the part
 *    the shopper is actually complaining about — and then stop.
 *
 * The opening state is removed the moment `dialog.quick-add-modal` gains its
 * `open` attribute, on `pointercancel` (the browser took the gesture for a
 * scroll, so no click is coming), and unconditionally after 4 s, so a failed
 * fetch leaves a normal button rather than a permanent spinner.
 *
 * Delegated from the document rather than bound per card: the collection grid
 * is re-rendered wholesale by the section renderer on every filter, sort and
 * page change, and a per-card binding would go inert exactly when a shopper is
 * deepest into browsing. Listeners are passive — nothing here calls
 * preventDefault, and the tap must stay a tap.
 *
 * Loaded from sections/echt-card-badges.liquid (collection, search, index —
 * every template that has a product grid).
 */

const BUTTON = 'button.quick-add__button';
const CHOOSE = 'quick-add__button--choose';
const DIALOG = '#quick-add-dialog dialog.quick-add-modal';
const PRESSED = 'echt-quick-add--pressed';
const OPENING = 'echt-quick-add--opening';

/** Longest the opening state may outlive the tap that started it. */
const OPENING_TIMEOUT = 4000;
/** How long a preload link is left in the head before it is cleaned up. */
const PRELOAD_LIFETIME = 10000;

/** URLs already warmed this page view — one speculative request each, at most. */
const warmed = new Set();

/** @type {HTMLElement | null} */
let pressed = null;
/** @type {number | undefined} */
let openingTimer;
let watchingDialog = false;

/**
 * Metered or slow connections get no speculative request. Same rule, and the
 * same reasoning, as assets/echt-card-video.js: a duplicate would compete for
 * bandwidth with the one the shopper is waiting on.
 * @returns {boolean}
 */
function connectionAllows() {
  const connection = navigator.connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  return !['slow-2g', '2g'].includes(connection.effectiveType);
}

/**
 * The URL stock will fetch on click.
 * Read from the component's own `productPageUrl` getter wherever the custom
 * element has upgraded, because that getter applies the card's selected
 * variant to the query string — warming a different URL than the one stock
 * asks for would guarantee a miss. The card link is the fallback for the
 * window before upgrade, where it is the same URL without `?variant=`.
 * @param {Element} button
 * @returns {string}
 */
function productPageUrl(button) {
  const component = /** @type {any} */ (button.closest('quick-add-component'));
  const fromComponent = component?.productPageUrl;
  if (typeof fromComponent === 'string' && fromComponent) return fromComponent;

  const link = /** @type {HTMLAnchorElement | null} */ (
    button.closest('product-card')?.querySelector('a.product-card__link') ?? null
  );
  return link?.href ?? '';
}

/**
 * Speculatively start the product-page request stock is about to make.
 * @param {string} url
 */
function warm(url) {
  if (!url || warmed.has(url) || !connectionAllows()) return;
  warmed.add(url);

  const link = document.createElement('link');
  link.rel = 'preload';
  // Destination "empty" + mode "cors" + credentials "same-origin" is the shape
  // of stock's bare `fetch(url)`; a preload only matches a request it matches
  // exactly, so these three are load-bearing, not decoration.
  link.as = 'fetch';
  link.crossOrigin = 'anonymous';
  link.fetchPriority = 'high';
  link.href = url;
  document.head.append(link);

  // The link has done its job the moment stock's fetch has matched it. Left in
  // the head it would only be a stale entry the browser keeps checking against.
  setTimeout(() => link.remove(), PRELOAD_LIFETIME);
}

/** Drop the pressed/opening classes from whichever button is wearing them. */
function clearState() {
  clearTimeout(openingTimer);
  if (!pressed) return;
  pressed.classList.remove(PRESSED, OPENING);
  pressed = null;
}

/**
 * Watch the one quick-add dialog for its `open` attribute, so the opening
 * state ends exactly when the shopper can see the picker. Attached lazily and
 * once: the dialog is rendered by layout/theme.liquid and never replaced.
 */
function watchDialog() {
  if (watchingDialog) return;
  const dialog = document.querySelector(DIALOG);
  if (!dialog) return;

  watchingDialog = true;
  new MutationObserver(() => {
    if (dialog.hasAttribute('open')) clearState();
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
}

/** @param {PointerEvent} event */
function onPointerDown(event) {
  // A mouse already has stock's `pointerenter` prefetch and a real hover state;
  // this is only for pointers that have neither.
  if (event.pointerType === 'mouse') return;

  const target = /** @type {Element | null} */ (event.target);
  const button = /** @type {HTMLElement | null} */ (target?.closest?.(BUTTON) ?? null);
  if (!button || button.closest('product-card') == null) return;

  clearState();
  pressed = button;
  button.classList.add(PRESSED);

  // Only the "Choose" button opens the dialog — "Add" posts the form and gets
  // its own checkmark from stock, so it must not wear a spinner. Selling-plan
  // cards navigate to the PDP instead of fetching, so there is nothing to warm.
  const component = button.closest('quick-add-component');
  if (!button.classList.contains(CHOOSE)) return;
  if (component instanceof HTMLElement && component.dataset.usesSellingPlans === 'true') return;

  button.classList.add(OPENING);
  watchDialog();
  openingTimer = setTimeout(clearState, OPENING_TIMEOUT);

  warm(productPageUrl(button));
}

document.addEventListener('pointerdown', onPointerDown, { passive: true });
// The press ends with the finger. `pointercancel` is the scroll case — the
// browser has taken the gesture, no click is coming, so the opening state must
// go with it.
document.addEventListener('pointerup', () => pressed?.classList.remove(PRESSED), { passive: true });
document.addEventListener('pointercancel', clearState, { passive: true });
